<!-- TVG-324/TVG-360/TVG-368: one compact line per viewing — venue and date,
     who is showing them around and the status, with Edit on the right.
     TVG-379: one line per viewing record (an event can hold several); the
     person and status show at every width, and Edit records them in place. -->
<div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
  <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6 sm:py-5">
    <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h3 class="text-base font-medium text-gray-800 dark:text-white/90">Viewings</h3>
      @if (venueTagText) {
        <span
          class="inline-flex shrink-0 items-center rounded-full bg-warning-50 px-2.5 py-0.5 text-theme-xs font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400"
          data-testid="contact-viewings-venue-tag">
          {{ venueTagText }}
        </span>
      }
    </div>
    @if (canCreateEvents) {
      <app-button
        [label]="'Book a viewing'"
        [style]="'primary'"
        data-testid="contact-book-show-round"
        (click)="bookViewing.emit()"
      ></app-button>
    }
  </div>

  <div class="border-t border-gray-100 py-2 dark:border-gray-800">
    @if (loadError) {
      <div class="mx-5 mb-3 rounded-md bg-error-50 p-3 text-theme-sm text-error-700 sm:mx-6">{{ loadError }}</div>
    }

    @if (showRounds.length > 0) {
      <div class="hidden grid-cols-[minmax(190px,1.6fr)_minmax(110px,1fr)_minmax(140px,1.2fr)_60px] gap-3 px-5 py-2 text-theme-xs text-gray-500 lg:grid dark:text-gray-400 sm:px-6">
        <span>Venue and date</span>
        <span>Show around</span>
        <span>Status</span>
        <span class="text-right">Edit</span>
      </div>

      @for (showRound of showRounds; track showRound.id) {
        <div
          class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-t border-gray-100 px-5 py-2.5 lg:grid-cols-[minmax(190px,1.6fr)_minmax(110px,1fr)_minmax(140px,1.2fr)_60px] dark:border-gray-800 sm:px-6"
          data-testid="contact-show-round-row">
          <span class="min-w-0">
            <span class="block truncate text-theme-sm font-semibold text-gray-900 dark:text-white">{{ showRound.venue?.name ?? 'Venue TBC' }}</span>
            <span class="block truncate text-theme-xs text-gray-500 dark:text-gray-400">
              {{ formatLongDate(showRound.date) }}@if (showRound.time) {, {{ showRound.time }}}
            </span>
          </span>
          <span class="col-span-2 truncate text-theme-sm text-gray-500 lg:col-span-1 dark:text-gray-400" data-testid="contact-show-round-person">
            <span class="lg:hidden">Show around: </span>{{ showRound.show_around_person ?? '—' }}
          </span>
          <!-- TVG-368: the status pill, with what the Outcome column used to
               show (the customer's post-viewing feedback, or why there is
               none yet) folded underneath it. -->
          <span class="col-span-2 min-w-0 lg:col-span-1" data-testid="contact-show-round-status">
            <span class="inline-flex rounded-full px-2.5 py-1 text-theme-xs font-medium" [class]="statusClasses(showRound)">
              {{ showRound.status_label }}
            </span>
            <span class="mt-1 block min-w-0" data-testid="contact-show-round-review">
              @let review = showRound.review;
              @if (showRound.outcome === 'booked') {
                <span class="text-theme-xs text-gray-500 dark:text-gray-400">Not yet held</span>
              } @else if (review) {
                <span class="flex flex-wrap gap-1">
                  <span class="inline-flex rounded-full px-2 py-0.5 text-theme-xs font-medium" [class]="reviewClasses(review.viewing_rating)" title="How was your viewing?">
                    Viewing: {{ review.viewing_rating_label }}
                  </span>
                  <span class="inline-flex rounded-full px-2 py-0.5 text-theme-xs font-medium" [class]="reviewClasses(review.venue_rating)" title="Did you like the venue?">
                    Venue: {{ review.venue_rating_label }}
                  </span>
                  <span class="inline-flex rounded-full px-2 py-0.5 text-theme-xs font-medium" [class]="reviewClasses(review.getting_married)" title="Can you see yourself getting married here?">
                    Getting married: {{ review.getting_married_label }}
                  </span>
                </span>
              } @else {
                <span class="text-theme-xs text-gray-500 dark:text-gray-400">No feedback yet</span>
              }
            </span>
          </span>
          <span class="col-start-2 row-start-1 text-right lg:col-start-auto lg:row-start-auto">
            <button
              type="button"
              class="text-theme-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
              data-testid="contact-show-round-edit"
              (click)="editingId === showRound.id ? cancelEdit() : startEdit(showRound)">
              Edit
            </button>
          </span>
        </div>

        @if (editingId === showRound.id) {
          <div class="flex flex-wrap items-end gap-3 border-t border-gray-100 bg-gray-50 px-5 py-3 dark:border-gray-800 dark:bg-white/[0.02] sm:px-6" data-testid="contact-show-round-editor">
            <label class="flex min-w-[180px] flex-col gap-1 text-theme-xs text-gray-500 dark:text-gray-400">
              Show around
              <select
                class="h-9 rounded-lg border border-gray-300 bg-white px-3 text-theme-sm text-gray-800 focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                data-testid="contact-show-round-person-select"
                [(ngModel)]="draftShowAroundNameId">
                <option [ngValue]="null">—</option>
                @for (choice of showAroundChoices(showRound); track choice.id) {
                  <option [ngValue]="choice.id">{{ choice.name }}</option>
                }
              </select>
            </label>
            <label class="flex min-w-[160px] flex-col gap-1 text-theme-xs text-gray-500 dark:text-gray-400">
              Outcome
              <select
                class="h-9 rounded-lg border border-gray-300 bg-white px-3 text-theme-sm text-gray-800 focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                data-testid="contact-show-round-outcome-select"
                [(ngModel)]="draftOutcome">
                @for (outcome of outcomes; track outcome.value) {
                  <option [ngValue]="outcome.value">{{ outcome.label }}</option>
                }
              </select>
            </label>
            <app-button
              [label]="saving ? 'Saving…' : 'Save'"
              [style]="'primary'"
              [disabled]="saving"
              data-testid="contact-show-round-save"
              (click)="saveEdit(showRound)"
            ></app-button>
            <app-button [label]="'Cancel'" [style]="'secondary'" (click)="cancelEdit()"></app-button>
            <a
              [routerLink]="['/events', showRound.event_id]"
              class="text-theme-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300">
              Open event
            </a>
            @if (saveError) {
              <p class="w-full text-theme-sm text-error-700">{{ saveError }}</p>
            }
          </div>
        }
      }
    } @else if (!loading && !loadError) {
      <p class="px-5 py-4 text-theme-sm text-gray-500 dark:text-gray-400 sm:px-6">
        No viewings have been booked for this contact yet.
      </p>
    }

    @if (loading) {
      <p class="px-5 py-4 text-theme-sm text-gray-500 dark:text-gray-400 sm:px-6">Loading viewings…</p>
    }
  </div>
</div>
